// Donut with total in the centre and a scrollable legend (colour dot, name, share %).
// Stacks vertically on mobile, donut + legend side by side from the sm breakpoint.
import * as React from "react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import type { CategorySlice } from "./CategoryDonutChart";

const COLORS = [
  "hsl(262 83% 58%)",
  "hsl(221 83% 63%)",
  "hsl(142 71% 45%)",
  "hsl(38 92% 50%)",
  "hsl(280 65% 60%)",
  "hsl(0 72% 60%)",
  "hsl(199 89% 48%)",
];

export interface SalesShareDonutChartProps {
  data: CategorySlice[];
  valueFormatter?: (v: number) => string;
}

const pct = (v: number) => (v > 0 && v < 1 ? "<1%" : `${Math.round(v)}%`);

export function SalesShareDonutChart({ data, valueFormatter }: SalesShareDonutChartProps) {
  const fmt = valueFormatter ?? ((v: number) => String(v));
  const total = React.useMemo(() => data.reduce((s, r) => s + r.amount, 0), [data]);

  return (
    <div className="flex h-full w-full flex-col items-center gap-4 sm:flex-row">
      <div className="relative h-44 w-44 shrink-0 sm:h-56 sm:w-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="amount" nameKey="category" innerRadius="62%" outerRadius="100%" paddingAngle={1} stroke="none">
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(v: number) => fmt(v)}
              contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xs text-muted-foreground">Total</span>
          <span className="text-base font-semibold text-foreground">{fmt(total)}</span>
        </div>
      </div>

      <ul className="max-h-48 w-full min-w-0 flex-1 space-y-2 overflow-y-auto pr-1 sm:max-h-56">
        {data.map((r, i) => (
          <li key={`${r.category}-${i}`} className="flex items-center gap-2 text-sm">
            <span className="h-3 w-3 shrink-0 rounded" style={{ background: COLORS[i % COLORS.length] }} />
            <span className="min-w-0 flex-1 truncate text-foreground" title={r.category}>{r.category}</span>
            <span className="shrink-0 font-semibold text-foreground">{pct(total > 0 ? (r.amount / total) * 100 : 0)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SalesShareDonutChart;
